"use client"

import Text from "@/components/common/Typography/Text"
import Title from "@/components/common/Typography/Title"
import UsersIcon from "@/components/icons/UsersIcon"
import { Button, Card, Col, Divider, Image, Row } from "antd"
import RoomOption from "./RoomOption"
import { useIsDesktop } from "@/hooks/app/useIsDesktop"
import { FC, useMemo, useRef } from "react"
import { HotelRoom } from "@/lib/redux/features/hotel/type"
import { useAppSelector } from "@/lib/redux/hooks"
import { useRoomResource } from "@/hooks/hotel/useRoomResource"
import { findIntersection } from "@/utils/app/findIntersection"
import AreaIcon from "@/components/icons/AreaIcon"
import { facilitiesIcon, viewsText } from "@/constants/hotel/facilities"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faPlusCircle, faStreetView } from "@fortawesome/free-solid-svg-icons"
import CheckedIcon from "@/components/icons/CheckedIcon"
import DoubleBedIcon from "@/components/icons/DoubleBedIcon"
import ModalDetailRoomFacilities, { ModalDetailRoomFacilitiesRef } from "./ModalDetailRoomFacilities"
import { useTETranslation } from "@/hooks/app/useTETranslation"
import { useTranslation } from "react-i18next"

const Room: FC<{ options: HotelRoom[], shownPrice?: boolean, index?: number, openLoginForm?: () => void }> = ({ options, index, shownPrice = true, openLoginForm }) => {
  const { getTranslatedText } = useTETranslation()
  const { t } = useTranslation()
  const isDesktop = useIsDesktop()
  // const router = useRouter()
  const childAndExtraBedPolicy = useAppSelector(state => state.hotel.hotels_detail.data.childAndExtraBedPolicy)
  const room = options[0]
  const resource = useRoomResource(room)
  // const dispatch = useAppDispatch()
  // const isAuthen = useIsAuthen()
  const refModalDetailRoomFacilities = useRef<ModalDetailRoomFacilitiesRef>(null)
  const facilities = Object.values(resource?.roomTypeFacility || {}).reduce((arr: any[], facilities1) => arr.concat(Object.values(facilities1)), [])

  const benifits = useMemo<any[]>(() => {
    const _benifits: any = {}
    const groupBenifits: any[][] = []
    if (options.length < 2) {
      return []
    }
    for (let i = 0; i < options.length; i++) {
      groupBenifits.push(options[i].benefits.map(b => b.id))
      for (let x = 0; x < options[i].benefits.length; x++) {
        const benifit = options[i].benefits[x];
        _benifits[benifit.id] = benifit
      }
    }
    return findIntersection(groupBenifits).map((benifitId) => _benifits[benifitId])
  }, [options])

  const bedTypes = useMemo(() => {
    const bedType = resource?.bedType || t("booking.1_double_bed")
    const _bedTypes = bedType.split(';')
    return _bedTypes.length <= 1 ? [bedType] : _bedTypes.map((b: string) => {
      const [name, type] = b.split(': :')
      if (!name || !type) {
        return [name, type].filter(v => !!v).join(': :')
      }
      return `- ${name} ${t("common.has")} ${type}`
    })
  }, [])

  const roomHeader = (
    <>
      <Row>
        <Title className="hotel__rooms--item-name" level={4}>
          {getTranslatedText(room?.translatedRoomName, room?.roomName)}
        </Title>
      </Row>
      <Row gutter={[15, 0]}>
        {[
          {
            text: t("hotel.room_available_for", { count: resource?.maxOccupancyPerRoom || 2 }),
            icon: <UsersIcon />,
            hidden: false
          },
          // {
          //   text: `${resource?.sizeOfRoom} m²`,
          //   icon: <AreaIcon />,
          //   hidden: !resource?.sizeOfRoom
          // },
          // {
          //   text: "Hướng: Biển",
          //   icon: <EyeIcon />
          // }
        ].filter(({ hidden }) => !hidden)
          .map(({ text, icon }) => {
            return (
              <Col>
                <Text type='secondary'>
                  {icon} {text}
                </Text>
              </Col>
            )
          })}
      </Row>
    </>
  )

  const roomOptions = (
    <>
      {options.map((option, idx) => {
        return (
          <>
            <RoomOption
              cheapest={index === 0 && idx === 0}
              showPrice={shownPrice}
              option={{
                ...option,
                benefits: option.benefits.filter(b => !benifits.find(bb => bb.id === b.id))
              }}
              index={idx}
              openLoginForm={openLoginForm}
            />
            {options.length - 1 > idx && <Divider style={{ marginBottom: '1rem', marginTop: '1.5rem' }} />}
          </>
        )
      })}
    </>
  )

  const images: any[] = Array.isArray(resource?.hotelRoomTypePictures?.hotelRoomTypePicture)
    ? resource.hotelRoomTypePictures.hotelRoomTypePicture
    : resource?.hotelRoomTypePictures?.hotelRoomTypePicture ? [resource?.hotelRoomTypePictures?.hotelRoomTypePicture] : []

  const firstImage = images[0]
  const nextThreeImages = images.slice(1, 7)

  const roomDetail = (
    <>
      <ModalDetailRoomFacilities ref={refModalDetailRoomFacilities} />
      <Image.PreviewGroup
        previewPrefixCls="preview__hotel"
        items={images.map(img => img.replace('s=312x', 's=1024x'))}
      >
        <Row gutter={[10, 10]}>
          {firstImage && (
            <Col span={24}>
              <Image
                src={firstImage?.replace('312x', '1024x')}
                style={{ width: "100%", height: '15.8rem' }}
                classNames={{
                  root: "hotel__rooms--item-img"
                }}
              />
            </Col>
          )}
          {nextThreeImages.map((img, idx) => {
            if (idx === nextThreeImages.length - 1) {
              return (
                <Col span={8} style={{ height: '8rem' }}>

                  <Image
                    classNames={{
                      root: "hotels__list--item-img--wrapper"
                    }}
                    className="hotels__list--item-img"
                    src={img.replace('312x', '1024x')}
                    styles={{ root: { height: '7.5rem', width: '100%' } }}
                    style={{ objectFit: 'cover', width: '100%', height: '100%' }}
                  />
                </Col>
              )
            }
            return (
              <Col span={8}>
                <Image
                  src={img.replace('312x', '1024x')}
                  classNames={{ root: "hotel__rooms--item-img" }}
                  style={{ width: "100%", height: '7.5rem' }}
                />
              </Col>
            )
          })}
        </Row>
      </Image.PreviewGroup>
      <Row style={{ marginBlock: '1rem' }}>
        <Text strong>{t("hotel.room_detail")}</Text>
      </Row>
      <Row gutter={[10, 10]}>
        <Col span={24}>
          <Row>
            <Text>
              <AreaIcon />{t("hotel.area_of_room")}: {`${resource?.sizeOfRoom} m²`}
            </Text>
          </Row>
        </Col>
        {!!resource?.views && (
          <Col span={24}>
            <Row>
              <Text>
                <FontAwesomeIcon icon={faStreetView} /> {viewsText[resource?.views] || resource?.views}
              </Text>
            </Row>
          </Col>
        )}
        <Col span={24}>
          <Row>
            <Text>
              <DoubleBedIcon /> {bedTypes.join(', ')}
            </Text>
          </Row>
        </Col>
        {facilities
          .slice(0, 5)
          .map(({ propertyName, propertyTranslatedName, propertyId }) => {
            return (
              <Col span={24}>
                <Row>
                  <Text>
                    {facilitiesIcon[propertyId] ? facilitiesIcon[propertyId] : <CheckedIcon />} {getTranslatedText(propertyTranslatedName, propertyName)}
                  </Text>
                </Row>
              </Col>
            )
          })}
        {facilities.slice(5).length > 0 && (
          <Button
            onClick={() => refModalDetailRoomFacilities.current?.open(room)}
            type='link'
            icon={<FontAwesomeIcon icon={faPlusCircle} />}
          >
            {t("hotel.other_facilities")}
          </Button>
        )}
      </Row>
    </>
  )

  const contentDesktop = (
    <>
      {roomHeader}
      <Divider style={{ marginBlock: '1.2rem' }} />
      <Row wrap={false}>
        <Col flex={"28.2rem"}>
          {roomDetail}
        </Col>
        <Col flex={1} style={{ paddingInline: '1.2rem' }}>
          {roomOptions}
        </Col>
      </Row>
    </>
  )

  const contentMobile = (
    <>
      {roomHeader}
      <Divider style={{ marginBlock: '1.2rem' }} />
      {roomDetail}
      <Row style={{ marginTop: '2rem' }}>
        <Col span={24}>
          {roomOptions}
        </Col>
      </Row>
    </>
  )

  return (
    <Card
      variant={!isDesktop ? 'borderless' : undefined}
      size='small'
      className="hotel__rooms--item"
      style={{ marginTop: 2, borderRadius: 0 }}
    >
      {isDesktop ? contentDesktop : contentMobile}
    </Card>
  )
}

export default Room